<template>
  <el-tour
    v-if="activeTour && steps.length > 0"
    v-model="open"
    :current="activeStepIndex"
    :mask="true"
    :show-arrow="true"
    :z-index="100000"
    placement="bottom"
    @change="onStepChange"
    @close="onClose"
  >
    <el-tour-step
      v-for="step in steps"
      :key="step.id"
      :target="step.target"
      :title="translate(step.title)"
    >
      <template #default>
        <div class="cc-tour-step-body">
          <p class="cc-tour-step-desc">{{ translate(step.desc) }}</p>
          <div class="cc-tour-step-actions">
            <a class="cc-tour-skip" @click="onSkip">{{ translate('Skip this tour') }}</a>
          </div>
        </div>
      </template>
    </el-tour-step>
  </el-tour>
</template>

<script>
/* eslint-disable */
import { mapState } from 'vuex'
import registry from '../definitions/_registry'

export default {
  name: 'CcTourRunner',

  data() {
    return { paused: false }
  },

  computed: {
    ...mapState({
      activeTourKey: (state) => state.Tours.activeTourKey,
      activeStepIndex: (state) => state.Tours.activeStepIndex,
      // Watch shopfloor's currentWorker so S1 can auto-resume.
      currentWorker: (state) => state.ShopFloorControl && state.ShopFloorControl.currentWorker,
    }),

    activeTour() {
      if (!this.activeTourKey) return null
      return registry[this.activeTourKey] || null
    },

    steps() {
      return this.activeTour ? this.activeTour.steps : []
    },

    isS1() {
      return this.activeTourKey === 'S1'
    },

    workerSelected() {
      return !!this.currentWorker && !!this.currentWorker.name
    },

    open: {
      get() {
        return this.activeTourKey != null && !this.paused
      },
      set(value) {
        // el-tour emits update:modelValue=false on close — handled by @close already; ignore here.
        if (!value && this.activeTourKey && !this.paused) {
          this.onClose()
        }
      },
    },
  },

  watch: {
    // Pause S1 only when the user ADVANCES from step 1 to step 2 without a worker selected.
    // When resuming from a saved progress row (oldIdx comes from a fresh activation, typically 0),
    // do NOT auto-pause — otherwise the tour pops up and immediately closes on every ? click.
    activeStepIndex(newIdx, oldIdx) {
      if (this.isS1 && newIdx === 2 && oldIdx === 1 && !this.workerSelected) {
        this.paused = true
      }
    },
    // Auto-resume S1 once a worker is picked.
    workerSelected(now) {
      if (now && this.isS1 && this.paused) {
        this.paused = false
      }
    },
    // Reset pause when tour ends.
    activeTourKey(newKey) {
      if (newKey == null) {
        this.paused = false
      }
    },
  },

  methods: {
    onStepChange(newIndex) {
      if (!this.activeTourKey) return
      // Persist current step so it can be resumed if the user closes the tour.
      this.$store.dispatch('Tours/updateProgress', {
        tourKey: this.activeTourKey,
        stepIndex: newIndex,
        status: 'InProgress',
      }).catch(() => {})
      this.$store.commit('Tours/TOURS_SET_ACTIVE', {
        tourKey: this.activeTourKey,
        stepIndex: newIndex,
      })
    },

    onClose() {
      if (!this.activeTourKey) return
      const key = this.activeTourKey
      // Element Plus 2.13 emits `close` even when the user clicks Finish on the last step
      // (no separate `finish` event in this version). Decide based on the current step index.
      const isLastStep = this.activeStepIndex >= this.steps.length - 1
      const action = isLastStep ? 'Tours/finishTour' : 'Tours/pauseTour'
      this.$store.dispatch(action, key).catch(() => {})
    },

    onSkip() {
      if (!this.activeTourKey) return
      const key = this.activeTourKey
      this.$store.dispatch('Tours/skipTour', key).catch(() => {})
    },
  },
}
</script>

<style>
/* Unscoped: el-tour teleports its popper to <body>, so scoped styles can't reach it.
   Element Plus 2.x does not accept `content-class` on <el-tour>, so we target the
   rendered `.el-tour__content` directly. Only one <el-tour> is used in this app
   (the A1 onboarding tour) so the global selector is safe.

   Dark-theme support: --background-color and --main-text-color are set by appSkin
   (see server/src/ProdFlop.Server.Services/docs/skins/tech-dark.json and the
   defaults in client/src/assets/sass/prodexis.css). */
.el-tour__content {
  background-color: var(--background-color, #ffffff) !important;
  color: var(--main-text-color, #303133) !important;
  /* Match the border style used on .el-dialog / .el-notification / .swal2-popup
     in prodexis.css so tour popups visually align with other app popups. */
  border: 1px solid var(--control-border-color, #dcdfe6) !important;
  border-radius: 4px !important;
}

.el-tour__arrow::before {
  border: 1px solid var(--control-border-color, #dcdfe6) !important;
  background-color: var(--background-color, #ffffff) !important;
}

.el-tour__content .el-tour__header,
.el-tour__content .el-tour__title {
  color: var(--main-text-color, #303133) !important;
}

.el-tour__content .el-tour__title {
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 1.4 !important;
}

.el-tour__content .el-tour__close {
  color: var(--main-text-color, #909399) !important;
}

.cc-tour-step-body {
  display: flex;
  flex-direction: column;
}

.cc-tour-step-desc {
  margin: 0 0 24px 0 !important;
  font-size: 16px !important;
  line-height: 1.6 !important;
  color: var(--main-text-color, #303133) !important;
}

.cc-tour-step-actions {
  display: flex;
  justify-content: flex-end;
}

.cc-tour-skip {
  font-size: 14px;
  cursor: pointer;
  color: var(--main-menu-active-text-color, #409eff);
  text-decoration: none;
}
.cc-tour-skip:hover {
  text-decoration: underline;
}

.el-tour__content .el-tour__footer .el-button {
  font-size: 14px;
  padding: 8px 16px;
}

/* Indicator dots: highlight the active one with the theme's accent color. */
.el-tour-indicator.is-active,
.el-tour__indicator.is-active {
  background-color: var(--main-menu-active-text-color, #409eff) !important;
}
</style>
